/**
 * MonitorPanel — a source's content monitor: the schedule that fetches its content, the analysis it runs, its health.
 * Subscribing a source = creating its monitor; unsubscribing = archiving it.
 * Template example: NextAdmin `profile/notification/page.tsx` (settings rows) in a card; demo `/connected-apps`
 * (connect, status badge, pause / disconnect).
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import {
  createIntelSourceMonitorMutation,
  fetchIntelSourceMonitorMutation,
  listIntelSourceMonitorsOptions,
  updateIntelSourceMonitorMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { zIntelSourceMonitorCreate } from '../../../generated/api/zod.gen.js';
import type { IntelSourceMonitor } from '../../../generated/api/types.gen.js';
import { Can } from '../../../components/auth/Can.js';
import { usePolicy } from '../../../hooks/usePolicy.js';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Callout } from '../../../components/ui/Callout.js';
import { Card } from '../../../components/ui/Card.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { Input, Select } from '../../../components/ui/Input.js';
import { SettingList, SettingRow } from '../../../components/ui/SettingRow.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { Toggle } from '../../../components/ui/Toggle.js';
import { useToast } from '../../../components/ui/Toast.js';
import { Form, SelectFormField, SubmitButton, SwitchFormField, useZodForm } from '../../../components/form';
import { FetchRunHistory } from './FetchRunHistory.js';
import { MonitorAnalysisSettings, MonitorLatestReport } from './MonitorAnalysis.js';
import { useMonitorInvalidation } from './intel-source-queries.js';
import { errorLabel, formatRelativeTime } from './SourceCard.js';

interface MonitorPanelProps {
  intelSourceId: number;
}

type Cadence = '@hourly' | '@4h' | '@daily' | '@weekly';

const CADENCE_OPTIONS: { value: Cadence; label: string }[] = [
  { value: '@hourly', label: 'Every hour' },
  { value: '@4h', label: 'Every 4 hours' },
  { value: '@daily', label: 'Daily' },
  { value: '@weekly', label: 'Weekly' },
];

const STATUS_LABEL: Record<IntelSourceMonitor['lifecycleStatus'], string> = {
  active: 'Active',
  paused: 'Paused',
  blocked: 'Blocked',
  archived: 'Archived',
};

function statusVariant(status: IntelSourceMonitor['lifecycleStatus']): 'success' | 'neutral' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'blocked') return 'danger';
  return 'neutral';
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-body">{children}</dd>
    </div>
  );
}

// ── Not monitored yet ────────────────────────────────────────────────────────────

const createSchema = zIntelSourceMonitorCreate.pick({ cadence: true, transcriptEnabled: true });

function UnmonitoredPanel({ intelSourceId }: { intelSourceId: number }) {
  const invalidate = useMonitorInvalidation();
  const form = useZodForm(createSchema, { defaultValues: { cadence: '@daily', transcriptEnabled: false } });
  const mutation = useMutation({ ...createIntelSourceMonitorMutation(), onSuccess: () => invalidate(intelSourceId) });

  return (
    <Card title="Monitor">
      <p className="mb-5 max-w-2xl text-sm text-muted">
        Monitor this source to fetch its content on a schedule. A monitor runs as a workflow; its runs and outputs are
        tracked under the content-monitor workflow.
      </p>
      <Can
        policy="CanManageAgents"
        fallback={<p className="text-sm text-muted">You need permission to manage agents to monitor this source.</p>}
      >
        <Form
          form={form}
          className="max-w-sm gap-4"
          onSubmit={(values) => mutation.mutateAsync({ path: { id: intelSourceId }, body: values })}
        >
          <SelectFormField name="cadence" label="Cadence" options={CADENCE_OPTIONS} />
          <SwitchFormField
            name="transcriptEnabled"
            label="Transcript extraction"
            description="Takes effect when transcript ingestion is enabled."
          />
          <div>
            <SubmitButton variant="primary" size="md">
              Create monitor
            </SubmitButton>
          </div>
        </Form>
      </Can>
    </Card>
  );
}

// ── Monitored ────────────────────────────────────────────────────────────────────

function MonitoredPanel({ monitor }: { monitor: IntelSourceMonitor }) {
  const canManage = usePolicy('CanManageAgents');
  const { notify } = useToast();
  const invalidate = useMonitorInvalidation();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const instanceId = monitor.workflowInstanceId;

  const updateMutation = useMutation({
    ...updateIntelSourceMonitorMutation(),
    onSuccess: () => invalidate(monitor.intelSourceId, instanceId),
    onError: () => notify('Could not update the monitor.', 'danger'),
  });
  const update = (patch: NonNullable<Parameters<typeof updateMutation.mutate>[0]['body']>) =>
    updateMutation.mutate({ path: { workflowInstanceId: instanceId }, body: patch });

  const fetchNowMutation = useMutation({
    ...fetchIntelSourceMonitorMutation(),
    onSuccess: () => {
      notify('Fetch started.');
      invalidate(monitor.intelSourceId, instanceId);
    },
    onError: (err) => {
      const alreadyRunning =
        err instanceof Error && (err.message.includes('409') || err.message.toLowerCase().includes('already running'));
      notify(alreadyRunning ? 'A fetch is already running.' : 'Could not start a fetch.', 'danger');
    },
  });

  const isActive = monitor.lifecycleStatus === 'active';
  const isPaused = monitor.lifecycleStatus === 'paused';
  const busy = !canManage || updateMutation.isPending;

  return (
    <div className="space-y-5">
      <Card
        title="Monitor"
        actions={
          <Badge variant={statusVariant(monitor.lifecycleStatus)} dot={isActive}>
            {STATUS_LABEL[monitor.lifecycleStatus]}
          </Badge>
        }
      >
        <SettingList>
          <SettingRow title="Cadence" description="How often the source is fetched.">
            <Select
              aria-label="Cadence"
              value={monitor.cadence}
              onChange={(e) => canManage && update({ cadence: e.target.value })}
              disabled={busy}
              className="w-48"
            >
              {CADENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </SettingRow>
          <SettingRow title="Transcript extraction" description="Takes effect when transcript ingestion is enabled.">
            <Toggle
              aria-label="Transcript extraction"
              checked={monitor.transcriptEnabled}
              onChange={(v) => canManage && update({ transcriptEnabled: v })}
              disabled={busy}
            />
          </SettingRow>
          <SettingRow
            title="Raw capture"
            description="Stores full raw payloads under the organization's retention window. For compliance use only."
          >
            <Toggle
              aria-label="Raw capture"
              checked={monitor.rawCaptureEnabled}
              onChange={(v) => canManage && update({ rawCaptureEnabled: v })}
              disabled={busy}
            />
          </SettingRow>
          <MonitorAnalysisSettings
            monitor={monitor}
            canManage={canManage}
            updating={updateMutation.isPending}
            onUpdate={update}
          />
          <SettingRow
            title="Monthly read budget"
            description="X ingestion is fail-closed without a budget. Leave empty to leave it unset."
          >
            <Input
              type="number"
              min={1}
              aria-label="Monthly read budget"
              defaultValue={monitor.monthlyReadBudget ?? ''}
              placeholder="Unset"
              disabled={busy}
              className="w-32"
              onBlur={(e) => {
                if (!canManage) return;
                const raw = e.target.value.trim();
                const next = raw === '' ? null : Number(raw);
                if (next !== (monitor.monthlyReadBudget ?? null) && (next === null || next > 0)) {
                  update({ monthlyReadBudget: next });
                }
              }}
            />
          </SettingRow>
        </SettingList>

        <dl className="mt-5 divide-y divide-border border-t border-border pt-2">
          <Fact label="Schedule">
            {monitor.scheduleEnabled ? (
              <>
                {CADENCE_OPTIONS.find((o) => o.value === monitor.cadence)?.label ?? monitor.cadence}
                {monitor.nextRunAt ? `, next ${formatRelativeTime(monitor.nextRunAt)}` : ''}
              </>
            ) : (
              <span className="text-faint">Off</span>
            )}
          </Fact>
          <Fact label="Last run">{formatRelativeTime(monitor.lastRunAt)}</Fact>
          <Fact label="Runs">{monitor.runCount}</Fact>
          <Fact label="Failures">
            {monitor.consecutiveFailures > 0 ? (
              <span className="text-danger">{monitor.consecutiveFailures} in a row</span>
            ) : (
              <span className="text-faint">None</span>
            )}
          </Fact>
          {monitor.blockedUntil && (
            <Fact label="Blocked until">
              <span className="text-warning">{new Date(monitor.blockedUntil).toLocaleString()}</span>
            </Fact>
          )}
          {monitor.lastErrorCode && (
            <Fact label="Last error">
              <span className="text-danger">{errorLabel(monitor.lastErrorCode)}</span>
            </Fact>
          )}
        </dl>

        <Can policy="CanManageAgents">
          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-5">
            {isActive && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => update({ lifecycleStatus: 'paused' })}
                disabled={updateMutation.isPending}
              >
                Pause
              </Button>
            )}
            {isPaused && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => update({ lifecycleStatus: 'active' })}
                disabled={updateMutation.isPending}
              >
                Resume
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={fetchNowMutation.isPending}
              onClick={() => fetchNowMutation.mutate({ path: { workflowInstanceId: instanceId } })}
            >
              <Zap size={14} aria-hidden="true" /> Fetch now
            </Button>
            <div className="ml-auto">
              <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(true)}>
                Stop monitoring
              </Button>
            </div>
          </div>
          <ConfirmDialog
            open={confirmArchive}
            onClose={() => setConfirmArchive(false)}
            onConfirm={() => {
              update({ lifecycleStatus: 'archived' });
              setConfirmArchive(false);
            }}
            title="Stop monitoring this source?"
            message="The monitor is archived and no more content is fetched on a schedule. Content already collected stays."
            confirmLabel="Stop monitoring"
            loading={updateMutation.isPending}
          />
        </Can>
      </Card>

      {monitor.latestRunId !== null && (
        <Card>
          <MonitorLatestReport latestRunId={monitor.latestRunId} />
        </Card>
      )}

      <FetchRunHistory workflowInstanceId={instanceId} />
    </div>
  );
}

// ── Public component ───────────────────────────────────────────────────────────

export const MonitorPanel: React.FC<MonitorPanelProps> = ({ intelSourceId }) => {
  const { data, isLoading, isError, refetch } = useQuery({
    ...listIntelSourceMonitorsOptions({ query: { intelSourceId, limit: 2 } }),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Card>
        <div className="space-y-2">
          <Skeleton height={18} width="40%" />
          <Skeleton height={14} />
          <Skeleton height={14} />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="Could not load the monitor"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Callout>
    );
  }

  // Prefer a live monitor; an archived one counts as not monitored.
  const matches = (data?.data ?? []).filter((m) => m.intelSourceId === intelSourceId);
  const monitor = matches.find((m) => m.lifecycleStatus !== 'archived');
  if (!monitor) return <UnmonitoredPanel intelSourceId={intelSourceId} />;
  return <MonitoredPanel monitor={monitor} />;
};
